import React, { useEffect, useState } from 'react';
import { Layout, Menu, Tabs, Form, Button, Modal } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import './HomePage.css';
import MwsEnvironmentSelector from "../components/MwsEnvironmentSelector";
import { ServiceInfoService } from "../http/ServiceInfoService";
import { ServiceInfo } from "../entities/ServiceInfo";
import ServiceInfoForm from '../components/ServiceInfoForm';
import { toast } from "react-toastify";
import PackagePage from "./PackagePage";
import DeployPage from "./DeployPage";
import PackageHistoryPage from "./PackageHistoryPage";
import {DEPLOY_ENV} from "../utils/config";
import DeployHistoryPage from "./DeployHistoryPage";

const { Header, Sider, Content } = Layout;

const HomePage = (): React.JSX.Element => {
    const [newServiceForm] = Form.useForm();
    const [editServiceForm] = Form.useForm();
    const [appList, setAppList] = useState<ServiceInfo[]>([]);
    const [selectedApp, setSelectedApp] = useState<ServiceInfo | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const appKey = params.get('appKey');

    useEffect(() => {
        ServiceInfoService.listServiceInfo().then((data) => {
            if (data) {
                setAppList(data);
            }
        });
    }, []);

    useEffect(() => {
        if (appList.length === 0) {
            return;
        }
        const foundApp = appList.find(app => app.appKey === appKey) || appList[0];
        setSelectedApp(foundApp);

        ServiceInfoService.getServiceInfo({ appKey: foundApp.appKey, env: DEPLOY_ENV}).then((response) => {
            if (response.isFailed() || !response.data) {
                return;
            }
            setSelectedApp(response.data);
        });
    }, [appList, appKey]);

    useEffect(() => {
        editServiceForm.setFieldsValue(selectedApp);
    }, [selectedApp, editServiceForm]);

    const handleAddService = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const createServiceInfo = async () => {
        try {
            const service = await newServiceForm.validateFields();
            const response = await ServiceInfoService.createServiceInfo(service);
            if (response.isFailed()) {
                return;
            }
            toast.success('新增成功！');
            setIsModalVisible(false);
            window.location.reload();
        } catch (error) {
            console.error('Failed to create service:', error);
        }
    };

    const updateServiceInfo = async () => {
        try {
            const values = await editServiceForm.validateFields();
            const service = { ...(selectedApp || {}), ...values } as ServiceInfo;
            const response = await ServiceInfoService.updateServiceInfo(service);
            if (response.isFailed()) {
                return;
            }
            setSelectedApp(service);
            setAppList(appList.map(app => (app.id === service.id ? service : app)));
            toast.success('更新成功！');
        } catch (error) {
            console.error('Failed to update service:', error);
        }
    };

    const handleTabChange = (key: string) => {
        const params = new URLSearchParams(location.search);
        params.set('tab', key);
        if (selectedApp) {
            params.set('appKey', selectedApp.appKey);
        }
        navigate({ search: params.toString() });
    };

    const handleMenuSelect = async ({ key }: { key: string }) => {
        const foundApp = appList.find(app => app.appKey === key) || null;
        setSelectedApp(foundApp);
        const params = new URLSearchParams(location.search);
        params.set('appKey', key);
        const tab = params.get('tab') || 'build';
        params.set('tab', tab);
        navigate({ search: params.toString() });
    };

    // 默认切到 build 或从 URL search 中读取
    const currentTab = new URLSearchParams(location.search).get('tab') || 'build';

    return (
        <Layout className="home-page">
            <Header className="home-page-header">
                <div className="header-title">
                    发布平台
                    <MwsEnvironmentSelector prefix="deploy" />
                </div>
            </Header>
            <Layout>
                <Sider className="home-page-sider" width="15%">
                    <Menu
                        className="service-list-menu"
                        mode="vertical"
                        selectedKeys={[selectedApp?.appKey || ""]}
                        onSelect={handleMenuSelect}
                        items={appList.map((app) => ({
                            key: app.appKey,
                            label: (
                                <div className="service-item">
                                    <div className="service-name">{app.name}</div>
                                    <div className="service-appKey">{app.appKey}</div>
                                </div>
                            ),
                        }))} />
                    <div className="add-service-button-container">
                        <Button type="dashed" onClick={handleAddService}>+ 添加服务</Button>
                    </div>
                </Sider>

                <Layout className="home-page-content-layout">
                    <Content className="home-page-content">
                        {selectedApp ? (
                            <Tabs
                                className="home-page-tabs"
                                activeKey={currentTab}
                                onChange={handleTabChange}
                                items={[
                                    { key: 'build', label: '构建', children: <PackagePage service={selectedApp} /> },
                                    { key: 'deploy', label: '部署', children: <DeployPage service={selectedApp} /> },
                                    { key: 'serviceInfo', label: '服务信息', children: (
                                            <>
                                                <ServiceInfoForm form={editServiceForm} serviceInfo={selectedApp} create={false} />
                                                <div className="fixed-footer">
                                                    <Button type="primary" onClick={updateServiceInfo}>更新</Button>
                                                </div>
                                            </>
                                        ) },
                                    { key: 'build-history', label: '构建历史', children: <PackageHistoryPage service={selectedApp} /> },
                                    { key: 'deploy-history', label: '部署历史', children: <DeployHistoryPage service={selectedApp} /> },
                                ]}
                            />
                        ) : (
                            <div className="no-data">没有服务</div>
                        )}
                    </Content>
                </Layout>
            </Layout>

            <Modal
                width="75%"
                open={isModalVisible}
                title="添加服务"
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        取消
                    </Button>,
                    <Button key="submit" type="primary" onClick={createServiceInfo}>
                        提交
                    </Button>,
                ]}
            >
                <ServiceInfoForm form={newServiceForm} serviceInfo={null} create={true} />
            </Modal>
        </Layout>
    );
};

export default HomePage;
